import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { LayoutUtilsService, MessageType } from '@app/core/_base/crud';
import { ModalCreateAttachmentComponent } from './modal-create-attachment/modal-create-attachment.component';
import { ModalUploadFileComponent } from './modal-upload-file/modal-upload-file.component';
import {
  Attachment,
  AttachmentCategory,
  ApiResponse,
  PaginatedResponse,
  DialogCloseResult
} from './attachment.interface';

@Component({
  selector: 'meu-attachment-management',
  templateUrl: './attachment-management.component.html',
  styleUrls: ['./attachment-management.component.scss']
})
export class AttachmentManagementComponent implements OnInit {
  displayedColumns: string[] = [
    'Index',
    'Image',
    'Name',
    'FileName',
    'Category',
    'Paid',
    'Actions'
  ];
  lstAllAttachment: Attachment[] = [];
  lstAllCategories: AttachmentCategory[] = [];
  total: number = 0;
  pageSize = 10;
  pageIndex = 1;
  host: string;
  filter: string = '';
  selectedCategoryId: string = '';
  searchText: string = '';
  pageSizeOptions: number[] = [5, 10, 20, 100];
  public keyUp = new Subject<string>();
  private apiUrl = environment.backendhost + '/api/attachments';
  private categoryApiUrl = environment.backendhost + '/api/attachment_categories';

  constructor(
    private layoutUtilsService: LayoutUtilsService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.keyUp
      .pipe(
        map((event) => event),
        debounceTime(500)
      )
      .subscribe((value: string) => {
        this.pageIndex = 1;
        this.searchText = value.trim();
        this.buildFilter();
        this.getAllAttachments();
      });
    this.loadCategories();
  }

  loadCategories() {
    const params = {
      Page: '1',
      PageSize: '1000'
    };
    this.http
      .get<ApiResponse<PaginatedResponse<AttachmentCategory>>>(this.categoryApiUrl, { params })
      .subscribe(
        (res) => {
          if (res.success && res.data && res.data.collection) {
            this.lstAllCategories = res.data.collection;
            this.getAllAttachments();
          }
        },
        (error) => {
          console.error('Error loading categories:', error);
          this.getAllAttachments();
        }
      );
  }

  buildFilter() {
    const filters: string[] = [];

    if (this.selectedCategoryId) {
      filters.push('attachment_category_id==' + this.selectedCategoryId);
    }

    this.filter = filters.join(',');
  }

  onCategoryChange(categoryId: string) {
    this.selectedCategoryId = categoryId;
    this.pageIndex = 1;
    this.buildFilter();
    this.getAllAttachments();
  }

  getAllAttachments() {
    const params: {
      Page: string;
      PageSize: string;
      Filters?: string;
      name?: string;
    } = {
      Page: this.pageIndex.toString(),
      PageSize: this.pageSize.toString()
    };

    if (this.searchText) {
      params.name = this.searchText;
    }

    if (this.filter) {
      params.Filters = this.filter;
    }

    this.http.get<ApiResponse<PaginatedResponse<Attachment>>>(this.apiUrl, { params }).subscribe(
      (res) => {
        if (res.success) {
          this.lstAllAttachment = res.data.collection;

          this.lstAllAttachment.forEach((attachment) => {
            if (attachment.attachment_category_id) {
              const category = this.lstAllCategories.find(
                (cat) => cat.id === attachment.attachment_category_id
              );
              if (category) {
                attachment.attachment_category = category;
              }
            }
          });

          this.total = res.data.total;
          this.host = environment.backendhost;
          this.ref.detectChanges();
        } else {
          this.toastr.error(res.message || 'Không thể tải danh sách file');
        }
      },
      (error) => {
        this.toastr.error('Không thể tải danh sách file');
      }
    );
  }

  onPageChange(pageNumber: number) {
    if (typeof pageNumber === 'number') {
      this.pageIndex = pageNumber;
      this.getAllAttachments();
      window.scrollTo(0, 0);
    }
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.getAllAttachments();
  }

  openModalAttachment(id: string | null) {
    const dialogRef = this.dialog.open(ModalCreateAttachmentComponent, {
      width: '900px',
      data: { id: id }
    });
    dialogRef.afterClosed().subscribe((result: DialogCloseResult | undefined) => {
      if (result != undefined && result.event != undefined) {
        if (result.event) {
          if (result.type == 'create') {
            this.toastr.success('Tạo mới file thành công');
            this.getAllAttachments();
          } else {
            this.toastr.success('Chỉnh sửa file thành công');
            this.getAllAttachments();
          }
        }
      }
    });
  }

  openUploadModal(id: string, name: string) {
    const dialogRef = this.dialog.open(ModalUploadFileComponent, {
      width: '600px',
      data: { id: id, name: name }
    });
    dialogRef.afterClosed().subscribe((result: DialogCloseResult | undefined) => {
      if (result != undefined && result.event != undefined) {
        if (result.event) {
          this.getAllAttachments();
        }
      }
    });
  }

  deleteAttachment(id: string, name: string) {
    const _title = 'Xóa file';
    const _description = 'Bạn có chắc chắn muốn xóa file ' + name + '?';
    const _waitDesciption = 'Đang xóa...';

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res: boolean) => {
      if (!res) {
        return;
      }

      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`).subscribe(
        (response) => {
          if (response.success) {
            this.getAllAttachments();
          } else {
            this.toastr.error(response.message || 'Không thể xóa file');
          }
        },
        (error) => {
          this.toastr.error('Có lỗi xảy ra khi xóa file');
        }
      );
    });
  }

  downloadFile(physicalPath: string, fileName: string) {
    if (!physicalPath) {
      this.toastr.warning('File không có sẵn để tải');
      return;
    }

    const downloadUrl = physicalPath.startsWith('/api/') ? this.host + physicalPath : physicalPath;

    window.open(downloadUrl, '_blank');
  }

  viewFile(physicalPath: string) {
    if (!physicalPath) {
      this.toastr.warning('File không có sẵn để xem');
      return;
    }

    const fileUrl = physicalPath.startsWith('/api/') ? this.host + physicalPath : physicalPath;

    window.open(fileUrl, '_blank');
  }
}
